// Core-line direction geometry for a single sample (mapmaking, grid missions).
//
// A core line has a *direction*: the path walks it from its first core to its
// last, so a half turn keeps the line where it is while swapping which end the
// robot enters from. Angles here are therefore full-turn (0–360, 0 = east, CCW)
// and coordinates are metric (UTM easting/northing) — the same frame
// `utils.rotatePoint` and `LineShape` work in.
import { GeometryPoint } from '../types/types';
import { rotatePoint } from '../utils';

/** A full turn: directions repeat every 360°. */
export const FULL_TURN_PERIOD_DEG = 360;

/** Distance (metres) below which two cluster points count as the same point. */
const DEGENERATE_LINE_EPSILON_METERS = 1e-9;

/** Fold any angle into the direction range [0, 360). */
export function normalizeFullTurnAngle(angleDeg: number): number {
  const mod = angleDeg % FULL_TURN_PERIOD_DEG;

  return mod < 0 ? mod + FULL_TURN_PERIOD_DEG : mod;
}

/**
 * Signed rotation (degrees, CCW) that takes a direction from `currentDeg` to
 * `targetDeg`, always the shorter way round: the result is in (-180, 180].
 *
 * The half turn is the interesting case — it is the flip that swaps the line's
 * ends — so it is kept as +180 rather than folded away.
 */
export function shortestRotationDelta(currentDeg: number, targetDeg: number): number {
  const raw = normalizeFullTurnAngle(targetDeg) - normalizeFullTurnAngle(currentDeg);
  const folded = ((((raw + 180) % FULL_TURN_PERIOD_DEG) + FULL_TURN_PERIOD_DEG) % FULL_TURN_PERIOD_DEG) - 180;

  // -180 and +180 are the same turn; keep the positive one for predictability.
  return folded === -180 ? 180 : folded;
}

/**
 * Bearing from `from` to `to` in degrees (0 = east, CCW, [0, 360)), or null
 * when the points coincide and so define no direction.
 */
export function directedBearingDeg(from: GeometryPoint, to: GeometryPoint): number | null {
  const east = to.x - from.x;
  const north = to.y - from.y;

  if (Math.hypot(east, north) < DEGENERATE_LINE_EPSILON_METERS) {
    return null;
  }

  return normalizeFullTurnAngle((Math.atan2(north, east) * 180) / Math.PI);
}

/** Rotate every point about `origin` by `deltaDeg` (CCW), keeping the shape rigid. */
export function rotateAboutOrigin(points: GeometryPoint[], origin: GeometryPoint, deltaDeg: number): GeometryPoint[] {
  return points.map((point) => rotatePoint(point, origin, deltaDeg));
}
